import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { SecureNote } from "@/components/site/Trust";
import { useSeo, SITE_URL } from "@/lib/seo";
import { LEGAL } from "@/lib/legal";
import { SHOP } from "@/lib/shop";
import { openCookieSettings } from "@/lib/consent";

const TODO = /(\[À (?:COMPLÉTER|VÉRIFIER)[^\]]*\])/;

function Text({ t }) {
  return t.split(TODO).map((part, i) => (TODO.test(part)
    ? <mark key={i} data-testid="legal-todo" className="rounded-sm bg-amber-300/90 px-1 text-[var(--ink)]">{part}</mark>
    : part));
}

function ContactCards() {
  return (
    <div className="mt-12 grid gap-4 sm:grid-cols-2">
      <a href={`tel:${SHOP.tel}`} data-testid="contact-phone" className="flex items-center gap-4 rounded-sm border border-[var(--gold)]/50 p-6 transition-colors hover:bg-[rgba(217,180,106,0.08)]">
        <Phone className="h-6 w-6 text-[var(--gold)]" /><span><span className="eyebrow block">Téléphone</span><span className="font-display text-2xl">{SHOP.phone}</span></span>
      </a>
      <a href={`mailto:${SHOP.email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-sm border border-[var(--line)] p-6 transition-colors hover:border-[var(--gold)]">
        <Mail className="h-6 w-6 text-[var(--gold)]" /><span><span className="eyebrow block">Email</span><span className="break-all text-lg">{SHOP.email}</span></span>
      </a>
    </div>
  );
}

export default function LegalPage({ id }) {
  const page = LEGAL[id];
  const crumbs = [{ to: "/", label: "Accueil" }, { to: page.path, label: page.label }];
  useSeo({ title: page.title, description: page.description, path: page.path, jsonLd: { "@context": "https://schema.org", "@graph": [breadcrumbLd(SITE_URL, crumbs)] } });
  return (
    <main data-testid={`legal-page-${id}`} className="mx-auto max-w-4xl px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={crumbs} />
      <h1 className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{page.h1}</h1>
      {page.highlight
        ? <p data-testid="legal-highlight" className="mt-6 rounded-sm border border-[var(--gold)] bg-[rgba(217,180,106,0.08)] p-6 text-base leading-relaxed text-[var(--parchment)] md:text-lg">{page.intro}</p>
        : <p className="mt-6 text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{page.intro}</p>}
      {JSON.stringify(page).includes("[À ") ? <p data-testid="legal-todo-notice" className="mt-6 text-xs text-[var(--parchment)]/80"><mark className="rounded-sm bg-amber-300/90 px-1 text-[var(--ink)]">Surligné</mark> : information à compléter ou à vérifier.</p> : null}
      {page.cookies ? <button type="button" onClick={openCookieSettings} data-testid="legal-cookie-settings" className="mt-8 flex h-12 items-center rounded-full bg-[var(--gold)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-colors hover:bg-[var(--gold-soft)]">Modifier mes choix cookies</button> : null}
      {page.contact ? <ContactCards /> : null}
      {id === "livraison" ? <SecureNote className="mt-10" testid="legal-secure-note" /> : null}
      <div className="mt-12 space-y-10">
        {page.sections.map((s) => (
          <section key={s.h} className="border-t border-[var(--line)] pt-8">
            <h2 className="font-display text-2xl italic text-[var(--gold-soft)]">{s.h}</h2>
            {s.p.map((t) => <p key={t} className="mt-3 text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base"><Text t={t} /></p>)}
          </section>
        ))}
      </div>
      {page.contact ? <p className="mt-10 text-sm"><Link to="/diagnostic-cheveux-afro" data-testid="contact-diagnostic-link" className="text-[var(--gold-soft)] underline underline-offset-4">Faire le diagnostic cheveux</Link></p> : null}
    </main>
  );
}
